import { useState } from "react";
import { nriBonusData, nriWhatsappUrl, nriStripeUrl } from "../../data/nriTaxData";
import { ChevronDown, ArrowRight } from "lucide-react";

const BonusRow = ({ bonus, index }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className={`border transition-all duration-300 rounded-sm overflow-hidden ${open ? "border-[#3f4816]/30 bg-[#302f2c]/60" : "border-[#3f4816]/15 bg-[#302f2c]/20 hover:border-[#3f4816]/30"}`}
      data-testid={`nri-bonus-${index}`}>
      <button onClick={() => setOpen(!open)} className="w-full flex items-center gap-4 px-5 py-4 text-left">
        {/* Bonus label */}
        <span className="text-[#d9fb06] text-[10px] font-black uppercase tracking-widest flex-shrink-0 w-16" style={{ fontFamily: "'Inter', Arial, sans-serif" }}>
          BONUS {bonus.number}
        </span>
        {/* Title */}
        <span className="text-[#dfddd6] font-bold text-sm flex-1 leading-snug" style={{ fontFamily: "'Inter', Arial, sans-serif" }}>{bonus.title}</span>
        {/* Value */}
        <div className="flex flex-col items-end flex-shrink-0 mr-3">
          <span className="text-[#888680]/40 text-[10px] line-through" style={{ fontFamily: "'Inter', Arial, sans-serif" }}>{bonus.value}</span>
          <span className="text-[#25D366] text-[10px] font-black" style={{ fontFamily: "'Inter', Arial, sans-serif" }}>FREE</span>
        </div>
        <ChevronDown size={15} className={`text-[#888680] flex-shrink-0 transition-transform duration-300 ${open ? "rotate-180 text-[#d9fb06]" : ""}`} />
      </button>
      <div className="overflow-hidden transition-all duration-300" style={{ maxHeight: open ? "200px" : "0", opacity: open ? 1 : 0 }}>
        <p className="px-5 pb-5 pt-0 pl-[88px] text-[#888680] text-xs leading-relaxed" style={{ fontFamily: "'Inter', Arial, sans-serif" }}>{bonus.body}</p>
      </div>
    </div>
  );
};

export const NriBonusSection = () => (
  <section id="nri-bonuses" className="relative py-28" style={{ background: "#1a1c1b" }}>
    <div className="absolute top-0 left-0 right-0 h-px bg-[#3f4816]/30" />
    <div className="max-w-[87.5rem] mx-auto px-10">

      {/* Heading */}
      <div className="mb-14">
        <span className="inline-block text-[#888680] text-xs font-medium uppercase tracking-[0.2em] mb-5" style={{ fontFamily: "'Inter', Arial, sans-serif" }}>
          {nriBonusData.sectionLabel}
        </span>
        <p className="text-[#888680]/60 font-semibold uppercase mb-1" style={{ fontFamily: "'Inter', Arial, sans-serif", fontSize: "clamp(0.85rem, 0.7rem + 0.6vw, 1rem)", letterSpacing: "-0.01em" }}>
          5 bonuses that make this
        </p>
        <h2 className="font-black uppercase leading-tight" style={{ fontFamily: "'Inter', Arial, sans-serif", fontSize: "clamp(1.8rem, 1.4rem + 2.5vw, 3.5rem)", letterSpacing: "-0.03em" }}>
          <span className="text-[#d9fb06]">A No-Brainer</span>
        </h2>
      </div>

      {/* Bonus accordions */}
      <div className="flex flex-col gap-2 mb-10 max-w-4xl">
        {nriBonusData.bonuses.map((b, i) => <BonusRow key={i} bonus={b} index={i} />)}
      </div>

      {/* Value summary — prominent strikethrough + real price */}
      <div className="max-w-4xl">
        <div className="bg-[#302f2c]/50 border border-[#d9fb06]/20 rounded-sm p-6">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
            {/* Left — value rows */}
            <div className="flex-1">
              <p className="text-[#888680] text-xs font-semibold uppercase tracking-widest mb-4" style={{ fontFamily: "'Inter', Arial, sans-serif" }}>Complete Value Stack</p>
              <div className="flex flex-col gap-2">
                {nriBonusData.valueSummary.map((row, i) => (
                  <div key={i} className="flex items-center justify-between gap-4">
                    <span className="text-[#888680]/70 text-xs" style={{ fontFamily: "'Inter', Arial, sans-serif" }}>{row.label}</span>
                    <span className="text-[#888680]/60 text-xs font-semibold" style={{ fontFamily: "'Inter', Arial, sans-serif" }}>{row.amount}</span>
                  </div>
                ))}
                <div className="h-px bg-[#d9fb06]/20 my-2" />
                <div className="flex items-center justify-between gap-4">
                  <span className="text-[#888680] text-xs font-semibold uppercase tracking-wider" style={{ fontFamily: "'Inter', Arial, sans-serif" }}>Total Value</span>
                  <span className="text-[#888680]/50 font-black text-base line-through" style={{ fontFamily: "'Inter', Arial, sans-serif", letterSpacing: "-0.02em" }}>
                    {nriBonusData.totalValue}
                  </span>
                </div>
              </div>
            </div>

            {/* Right — your price */}
            <div className="flex flex-col items-start md:items-end pt-4 md:pt-0 border-t md:border-t-0 md:border-l border-[#3f4816]/20 md:pl-8">
              <p className="text-[#888680]/50 text-[10px] uppercase tracking-widest mb-3" style={{ fontFamily: "'Inter', Arial, sans-serif" }}>Your investment</p>
              {/* Three prices stacked */}
              <div className="flex flex-col items-start md:items-end gap-0.5 mb-3">
                <p className="text-[#d9fb06] font-black leading-none" style={{ fontFamily: "'Inter', Arial, sans-serif", fontSize: "clamp(2rem, 1.6rem + 1.5vw, 3rem)", letterSpacing: "-0.04em" }}>
                  ₹14,999
                </p>
                <p className="text-[#888680]/50 text-sm font-semibold leading-tight" style={{ fontFamily: "'Inter', Arial, sans-serif" }}>
                  $160 &nbsp;·&nbsp; 599 AED
                </p>
              </div>
              <p className="text-[#25D366] text-xs font-semibold mb-4" style={{ fontFamily: "'Inter', Arial, sans-serif" }}>Everything included</p>
              <a href={nriStripeUrl} target="_blank" rel="noopener noreferrer" data-testid="nri-bonus-cta"
                className="group inline-flex items-center gap-2 bg-[#d9fb06] text-[#1a1c1b] font-bold text-xs px-6 py-3.5 rounded-full transition-all duration-300 hover:scale-[1.02] hover:opacity-90 uppercase tracking-tight"
                style={{ fontFamily: "'Inter', Arial, sans-serif" }}>
                Get My NRI Tax Report + Filing
                <ArrowRight size={13} className="transition-transform group-hover:translate-x-0.5 duration-300" />
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
);
